import { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Table, Input, InputNumber, Select, Button, Tag, Modal, Segmented, Space, Typography,
  Form, Row, Col, Card, Statistic, Alert, App as AntApp, Flex, DatePicker,
} from 'antd';
import {
  PlusOutlined, EyeOutlined, DeleteOutlined, CheckCircleFilled, CheckCircleOutlined,
  EditOutlined, UndoOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { API_URL } from '../config';
import { hojeIso } from '../utils/dataFmt';
import { usePerms } from '../hooks/usePerms';
import { PROPS_DINHEIRO_BR } from '../utils/dinheiroInput';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

const fmt = (v: number) =>
  'R$ ' + (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (s: string | null) => {
  if (!s) return '—';
  const [y, m, d] = s.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

interface Emprestimo {
  id: string;
  instituicao: string;
  identificador: string | null;
  valor_principal: string;
  data_contratacao: string;
  prazo_meses: number;
  taxa_mensal_pct: string;
  sistema: 'PRICE' | 'SAC' | 'CONSORCIO';
  carencia_meses: number;
  iof_total: string;
  observacoes: string | null;
  data_quitacao: string | null;
  status: 'ativo' | 'quitado';
  tipo_contrato: string;
  taxa_label: string;
  indice_mensal_pct: string;
  custos_iniciais: string;
  taxa_seguro_mensal_pct: string;
  cronograma_origem: 'calculado' | 'importado';
  juros_total: string;
  juros_pagos: string;
  principal_pago: string;
  saldo_devedor_atual: string;
  total_parcelas: number;
  parcelas_pagas: number;
}

const TIPOS_CONTRATO = [
  { value: 'emprestimo', label: 'Empréstimo bancário', taxaLabel: 'Juros', sistema: 'PRICE' },
  { value: 'mutua', label: 'Mútuo / Empréstimo com índice', taxaLabel: 'Juros', sistema: 'PRICE' },
  { value: 'financiamento', label: 'Financiamento (veículo/imóvel)', taxaLabel: 'Juros', sistema: 'PRICE' },
  { value: 'consorcio', label: 'Consórcio', taxaLabel: 'Taxa de administração', sistema: 'CONSORCIO' },
];

interface Parcela {
  id: string;
  numero: number;
  data_vencimento: string;
  valor_juros: string;
  valor_principal: string;
  valor_parcela: string;
  saldo_devedor: string;
  paga: boolean;
  data_pagamento: string | null;
}

type StatusFiltro = 'ativos' | 'quitados' | 'todos';

function EmprestimosInner() {
  const { message, modal } = AntApp.useApp();
  const [emps, setEmps] = useState<Emprestimo[]>([]);
  const [loading, setLoading] = useState(true);
  // Os codigos financeiro.emprestimos.editar/.excluir existem no catalogo desde
  // a migration 1779897530993 e nenhuma tela os usava — a pagina inteira nao
  // tinha gate. 🚨 Falha ao CARREGAR a matriz nao pode virar "voce nao pode"
  // (incidente de 01/09): o gate de verdade e o do servidor, entao aqui erro
  // deixa passar.
  const { pode, erro: permsErro } = usePerms();
  const podeEditar = permsErro || pode('financeiro.emprestimos.editar') || pode('admin.permissoes.editar');
  const podeExcluir = permsErro || pode('financeiro.emprestimos.excluir') || pode('admin.permissoes.editar');
  const [filtroStatus, setFiltroStatus] = useState<StatusFiltro>('ativos');
  const [mostrarNovo, setMostrarNovo] = useState(false);
  const [verParcelas, setVerParcelas] = useState<Emprestimo | null>(null);
  // Pedido do Lucas (08/09/2026): "preciso conseguir editar o empréstimo
  // contratado". O mesmo formulário serve para criar e para editar.
  const [editando, setEditando] = useState<Emprestimo | null>(null);

  useEffect(() => { carregar(); }, [filtroStatus]);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const qs = filtroStatus === 'todos' ? '' : `?status=${filtroStatus}`;
      const res = await axios.get(`${API_URL}/emprestimos${qs}`, { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setEmps(data);
    } finally { setLoading(false); }
  }

  function excluir(id: string) {
    modal.confirm({
      title: 'Excluir empréstimo?',
      content: 'Esta ação remove também todas as parcelas associadas.',
      okText: 'Excluir',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.delete(`${API_URL}/emprestimos/${id}`, { headers: { Authorization: token } });
          message.success('Empréstimo excluído.');
          await carregar();
        } catch (e: any) { message.error('Erro: ' + e.message); }
      },
    });
  }

  const totais = emps.reduce((acc, e) => {
    acc.emprestado += parseFloat(e.valor_principal);
    acc.saldo_devedor += parseFloat(e.saldo_devedor_atual);
    acc.juros_totais += parseFloat(e.juros_total);
    acc.juros_pagos += parseFloat(e.juros_pagos);
    return acc;
  }, { emprestado: 0, saldo_devedor: 0, juros_totais: 0, juros_pagos: 0 });

  const columns: ColumnsType<Emprestimo> = [
    {
      title: 'Instituição / Contrato', dataIndex: 'instituicao', key: 'inst',
      render: (v, r) => (
        <>
          <Text strong style={{ color: '#020D7F' }}>{v}</Text>
          {r.identificador && <div style={{ fontSize: 10, color: '#888' }}>{r.identificador}</div>}
        </>
      ),
    },
    {
      title: 'Contratação', dataIndex: 'data_contratacao', key: 'contr', width: 100, align: 'center',
      render: (v) => <Text type="secondary" style={{ fontSize: 11 }}>{fmtDate(v)}</Text>,
    },
    {
      title: 'Principal', dataIndex: 'valor_principal', key: 'principal', width: 120, align: 'right',
      render: (v) => fmt(parseFloat(v)),
    },
    {
      title: 'Prazo', dataIndex: 'prazo_meses', key: 'prazo', width: 70, align: 'center',
      render: (v) => `${v}m`,
    },
    {
      title: 'Taxa', dataIndex: 'taxa_mensal_pct', key: 'taxa', width: 70, align: 'center',
      render: (v) => `${parseFloat(v).toFixed(3)}%`,
    },
    {
      title: 'Sist.', dataIndex: 'sistema', key: 'sist', width: 70, align: 'center',
      render: (v) => <Text style={{ fontSize: 11 }}>{v}</Text>,
    },
    {
      title: 'Parcelas', key: 'parc', width: 90, align: 'center',
      render: (_, r) => <Text style={{ fontSize: 11 }}>{r.parcelas_pagas} / {r.total_parcelas}</Text>,
    },
    {
      title: 'Saldo dev.', dataIndex: 'saldo_devedor_atual', key: 'saldo', width: 120, align: 'right',
      render: (v) => <Text strong style={{ color: '#B91626' }}>{fmt(parseFloat(v))}</Text>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 90, align: 'center',
      render: (s: string) => s === 'quitado'
        ? <Tag color="default" style={{ margin: 0 }}>QUITADO</Tag>
        : <Tag color="success" style={{ margin: 0 }}>Ativo</Tag>,
    },
    {
      title: 'Ações', key: 'acoes', width: 220, align: 'center',
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => setVerParcelas(r)}>
            Parcelas
          </Button>
          {podeEditar && (
            <Button size="small" icon={<EditOutlined />} onClick={() => setEditando(r)}>
              Editar
            </Button>
          )}
          {podeExcluir && (
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => excluir(r.id)} />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px 24px' }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <Title level={4} style={{ margin: 0, color: '#020D7F' }}>
            Empréstimos e Financiamentos
          </Title>
          <Space wrap>
            <Segmented
              value={filtroStatus}
              onChange={(v) => setFiltroStatus(v as StatusFiltro)}
              options={[
                { label: 'Ativos', value: 'ativos' },
                { label: 'Quitados', value: 'quitados' },
                { label: 'Todos', value: 'todos' },
              ]}
              size="small"
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setMostrarNovo(true)}>
              Novo empréstimo
            </Button>
          </Space>
        </Flex>

        <Row gutter={10}>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Total emprestado"
                value={totais.emprestado}
                prefix="R$"
                precision={2}
                valueStyle={{ color: '#020D7F', fontSize: 18 }}
              />
              <Text type="secondary" style={{ fontSize: 10 }}>{emps.length} contratos</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Saldo devedor atual"
                value={totais.saldo_devedor}
                prefix="R$"
                precision={2}
                valueStyle={{ color: '#B91626', fontSize: 18 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Juros totais contratados"
                value={totais.juros_totais}
                prefix="R$"
                precision={2}
                valueStyle={{ color: '#B07500', fontSize: 18 }}
              />
              <Text type="secondary" style={{ fontSize: 10 }}>Pagos: {fmt(totais.juros_pagos)}</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Juros a pagar"
                value={totais.juros_totais - totais.juros_pagos}
                prefix="R$"
                precision={2}
                valueStyle={{ color: '#B07500', fontSize: 18 }}
              />
            </Card>
          </Col>
        </Row>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={emps}
          loading={loading}
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true }}
          rowClassName={(r) => r.status === 'quitado' ? 'row-fechado' : ''}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'Nenhum empréstimo neste filtro.' }}
        />
      </Space>

      {mostrarNovo && (
        <FormNovo onClose={() => setMostrarNovo(false)} onSaved={async () => { setMostrarNovo(false); await carregar(); }} />
      )}
      {editando && (
        <FormNovo
          emp={editando}
          onClose={() => setEditando(null)}
          onSaved={async () => { setEditando(null); await carregar(); }}
        />
      )}
      {verParcelas && (
        <ModalParcelas emp={verParcelas} onClose={() => setVerParcelas(null)} onChanged={carregar} />
      )}
    </div>
  );
}

// Rotulo e equivalente anual da taxa — fora do JSX so pra o formulario nao
// carregar template literal no meio da arvore.
const TAXA_LABEL = (f: any) => `${f.taxa_label} mensal (%)`;
const EQUIV_ANUAL = (pct: any) =>
  `Equivalente anual: ${pct ? (((1 + parseFloat(pct) / 100) ** 12 - 1) * 100).toFixed(2) : 0}%`;

function FormNovo({ onClose, onSaved, emp }: { onClose: () => void; onSaved: () => void; emp?: Emprestimo }) {
  const { message } = AntApp.useApp();
  const editando = !!emp;
  // Cronograma que veio do banco não se recalcula pela fórmula: os campos que
  // definiriam a tabela ficam travados, e a tela diz por quê. Quem manda é o
  // papel do banco (docs/emprestimos.md).
  const cronogramaDoBanco = emp?.cronograma_origem === 'importado';
  const temParcelaPaga = (emp?.parcelas_pagas || 0) > 0;
  const travaCronograma = cronogramaDoBanco || temParcelaPaga;
  const [form, setForm] = useState<any>(emp ? {
    tipo_contrato: emp.tipo_contrato || 'emprestimo',
    taxa_label: emp.taxa_label || 'Juros',
    instituicao: emp.instituicao || '',
    identificador: emp.identificador || '',
    valor_principal: emp.valor_principal,
    data_contratacao: String(emp.data_contratacao || '').slice(0, 10),
    prazo_meses: emp.prazo_meses,
    taxa_mensal_pct: emp.taxa_mensal_pct,
    indice_mensal_pct: emp.indice_mensal_pct,
    taxa_seguro_mensal_pct: emp.taxa_seguro_mensal_pct,
    custos_iniciais: emp.custos_iniciais,
    sistema: emp.sistema,
    carencia_meses: emp.carencia_meses,
    iof_total: emp.iof_total,
    observacoes: emp.observacoes || '',
  } : {
    tipo_contrato: 'emprestimo',
    taxa_label: 'Juros',
    instituicao: '',
    identificador: '',
    valor_principal: '',
    data_contratacao: hojeIso(),
    prazo_meses: 36,
    taxa_mensal_pct: '1.5',
    indice_mensal_pct: 0,
    taxa_seguro_mensal_pct: 0,
    custos_iniciais: 0,
    sistema: 'PRICE',
    carencia_meses: 0,
    iof_total: 0,
    observacoes: '',
  });

  function aplicarTipoContrato(tipo: string) {
    const t = TIPOS_CONTRATO.find(x => x.value === tipo);
    if (!t) return;
    setForm((f: any) => ({ ...f, tipo_contrato: tipo, taxa_label: t.taxaLabel, sistema: t.sistema }));
  }

  const [preview, setPreview] = useState<any[] | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function gerarPreview() {
    if (!form.valor_principal || !form.prazo_meses || !form.taxa_mensal_pct) return;
    // Editando um cronograma travado, a prévia da fórmula seria uma tabela que
    // NÃO é a do contrato — pior que nenhuma.
    if (travaCronograma) return;
    try {
      const token = await getToken();
      const res = await axios.post(`${API_URL}/emprestimos/preview`, {
        valor_principal: parseFloat(form.valor_principal),
        prazo_meses: parseInt(form.prazo_meses),
        taxa_mensal_pct: parseFloat(form.taxa_mensal_pct),
        indice_mensal_pct: parseFloat(form.indice_mensal_pct) || 0,
        taxa_seguro_mensal_pct: parseFloat(form.taxa_seguro_mensal_pct) || 0,
        sistema: form.sistema,
        carencia_meses: parseInt(form.carencia_meses) || 0,
        data_contratacao: form.data_contratacao,
      }, { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setPreview(data.parcelas);
    } catch {}
  }

  useEffect(() => {
    const t = setTimeout(gerarPreview, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [form.valor_principal, form.prazo_meses, form.taxa_mensal_pct, form.indice_mensal_pct, form.taxa_seguro_mensal_pct, form.sistema, form.carencia_meses, form.data_contratacao]);

  const totais = preview ? preview.reduce((a, p) => ({
    juros: a.juros + p.juros,
    principal: a.principal + p.principal,
    parcela_sum: a.parcela_sum + p.parcela,
  }), { juros: 0, principal: 0, parcela_sum: 0 }) : null;

  async function salvar() {
    if (!form.instituicao || !form.valor_principal || !form.prazo_meses || !form.taxa_mensal_pct) {
      message.warning('Preencha os campos obrigatórios');
      return;
    }
    setSalvando(true);
    try {
      const token = await getToken();
      const corpo = {
        ...form,
        valor_principal: parseFloat(form.valor_principal),
        prazo_meses: parseInt(form.prazo_meses),
        taxa_mensal_pct: parseFloat(form.taxa_mensal_pct),
        indice_mensal_pct: parseFloat(form.indice_mensal_pct) || 0,
        taxa_seguro_mensal_pct: parseFloat(form.taxa_seguro_mensal_pct) || 0,
        custos_iniciais: parseFloat(form.custos_iniciais) || 0,
        carencia_meses: parseInt(form.carencia_meses) || 0,
        iof_total: parseFloat(form.iof_total) || 0,
      };
      if (editando) {
        await axios.put(`${API_URL}/emprestimos/${emp!.id}`, corpo, { headers: { Authorization: token } });
      } else {
        await axios.post(`${API_URL}/emprestimos`, corpo, { headers: { Authorization: token } });
      }
      message.success(editando ? 'Empréstimo atualizado.' : 'Empréstimo criado.');
      onSaved();
    } catch (e: any) {
      // O servidor recusa com FRASE (parcela já paga, cronograma do banco,
      // tabela que não fecha). Mostrar `e.message` engoliria tudo isso num
      // "Request failed with status code 409".
      const d = e.response?.data;
      const corpo = d?.body ? (() => { try { return JSON.parse(d.body); } catch { return d; } })() : d;
      message.error(corpo?.error || e.message, 8);
    }
    finally { setSalvando(false); }
  }

  return (
    <Modal
      open
      onCancel={onClose}
      onOk={salvar}
      okText={editando ? 'Salvar alterações' : 'Criar empréstimo'}
      cancelText="Cancelar"
      confirmLoading={salvando}
      title={editando ? `Editar empréstimo · ${emp!.instituicao}` : 'Novo Empréstimo'}
      // Regra da casa (CLAUDE.md): modal não obriga a rolar tendo tela sobrando.
      // Era width={900} com 13 campos empilhados de dois em dois e o cronograma
      // embaixo — nove andares, e o botão de criar caía fora da dobra num
      // notebook (relato do Lucas, 08/09/2026). Agora o contrato e o cronograma
      // ficam LADO A LADO e o corpo só rola no que passar da tela.
      width="94vw"
      style={{ maxWidth: 1280, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' } }}
      destroyOnClose
    >
      <Form layout="vertical" size="small">
        <Row gutter={24}>
        {/* ——— Coluna A: o contrato ——— */}
        <Col xs={24} lg={13}>
        <Row gutter={12}>
          <Col xs={24} sm={12} xl={8}>
            <Form.Item
              label="Tipo de contrato"
              required
              extra={
                <>
                  {form.tipo_contrato === 'consorcio' && 'Consórcio: amortização linear + taxa de administração mensal.'}
                  {form.tipo_contrato === 'mutua' && 'Mútuo: taxa de juros + índice adicional.'}
                </>
              }
            >
              <Select
                value={form.tipo_contrato}
                onChange={aplicarTipoContrato}
                options={TIPOS_CONTRATO.map(t => ({ value: t.value, label: t.label }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={8}>
            <Form.Item label="Sistema" required>
              <Select
                disabled={travaCronograma}
                value={form.sistema}
                onChange={(v) => setForm({ ...form, sistema: v })}
                options={[
                  { value: 'PRICE', label: 'PRICE (parcelas fixas)' },
                  { value: 'SAC', label: 'SAC (amortização constante)' },
                  { value: 'CONSORCIO', label: 'CONSÓRCIO (amortização linear)' },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={8}>
            <Form.Item label="Data de contratação" required>
              <DatePicker
                disabled={travaCronograma}
                value={form.data_contratacao ? dayjs(form.data_contratacao) : null}
                onChange={(d: Dayjs | null) => setForm({ ...form, data_contratacao: d ? d.format('YYYY-MM-DD') : '' })}
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} xl={16}>
            <Form.Item label="Instituição" required>
              <Input
                value={form.instituicao}
                onChange={e => setForm({ ...form, instituicao: e.target.value })}
                placeholder="Ex.: Banco do Brasil / Mútua / Embracon"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={8}>
            <Form.Item label="Identificador (contrato)">
              <Input
                value={form.identificador}
                onChange={e => setForm({ ...form, identificador: e.target.value })}
                placeholder="Ex.: 12345-6"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} xl={8}>
            <Form.Item label="Valor principal" required>
              <InputNumber {...PROPS_DINHEIRO_BR}
                disabled={travaCronograma}
                value={form.valor_principal}
                onChange={(v) => setForm({ ...form, valor_principal: v })}
                style={{ width: '100%' }}
                prefix="R$"
                precision={2}
                min={0}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={8}>
            <Form.Item label="Prazo (meses)" required>
              <InputNumber
                disabled={travaCronograma}
                value={form.prazo_meses}
                onChange={(v) => setForm({ ...form, prazo_meses: v })}
                style={{ width: '100%' }}
                min={1}
                max={600}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={8}>
            <Form.Item
              label={TAXA_LABEL(form)}
              required
              extra={
                form.tipo_contrato === 'consorcio'
                  ? 'Taxa administrativa mensal do consórcio'
                  : EQUIV_ANUAL(form.taxa_mensal_pct)
              }
            >
              <InputNumber
                disabled={travaCronograma}
                value={form.taxa_mensal_pct}
                onChange={(v) => setForm({ ...form, taxa_mensal_pct: v })}
                style={{ width: '100%' }}
                step={0.001}
                min={0}
                suffix="%"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} xl={8}>
            <Form.Item label="Carência (meses só-encargo)">
              <InputNumber
                disabled={travaCronograma}
                value={form.carencia_meses}
                onChange={(v) => setForm({ ...form, carencia_meses: v })}
                style={{ width: '100%' }}
                min={0}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={8}>
            <Form.Item label="Índice mensal (%)" extra="IPCA/TR/CDI · fundo de reserva no consórcio">
              <InputNumber
                disabled={travaCronograma}
                value={form.indice_mensal_pct}
                onChange={(v) => setForm({ ...form, indice_mensal_pct: v })}
                style={{ width: '100%' }}
                step={0.001}
                min={0}
                suffix="%"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={8}>
            <Form.Item label="Seguro mensal (%)" extra="Prestamista/quebra de garantia (opcional)">
              <InputNumber
                disabled={travaCronograma}
                value={form.taxa_seguro_mensal_pct}
                onChange={(v) => setForm({ ...form, taxa_seguro_mensal_pct: v })}
                style={{ width: '100%' }}
                step={0.001}
                min={0}
                suffix="%"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} xl={8}>
            <Form.Item
              label="Custos iniciais (R$)"
              extra="Taxa de adesão / cadastro · lançados como despesa na data de contratação"
            >
              <InputNumber {...PROPS_DINHEIRO_BR}
                value={form.custos_iniciais}
                onChange={(v) => setForm({ ...form, custos_iniciais: v })}
                style={{ width: '100%' }}
                precision={2}
                min={0}
                prefix="R$"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xl={8}>
            <Form.Item label="IOF total (R$)" extra="Entra na DRE junto dos custos iniciais">
              <InputNumber {...PROPS_DINHEIRO_BR}
                value={form.iof_total}
                onChange={(v) => setForm({ ...form, iof_total: v })}
                style={{ width: '100%' }}
                precision={2}
                min={0}
                prefix="R$"
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label="Observações" style={{ marginBottom: 8 }}>
              <Input.TextArea
                value={form.observacoes}
                onChange={e => setForm({ ...form, observacoes: e.target.value })}
                rows={2}
              />
            </Form.Item>
          </Col>
        </Row>

        <Alert
          type="warning"
          showIcon
          message={
            <Text style={{ fontSize: 11 }}>
              Somente a <strong>parcela de juros</strong> entra na DRE (Despesas Financeiras). O principal reduz o passivo (não-despesa).
              Lembre-se de marcar a categoria "Empréstimos e Dívidas" do ContaAzul como <strong>ignorada</strong> para evitar dupla contagem.
            </Text>
          }
        />
        </Col>

        {/* ——— Coluna B: o cronograma que o sistema propõe ——— */}
        <Col xs={24} lg={11}>
          {editando && travaCronograma ? (
            // Editando um contrato que não pode ter o cronograma recalculado:
            // a prévia da fórmula seria uma tabela que NÃO é a do contrato.
            // Em vez dela, o retrato do cronograma que existe + o porquê da trava.
            <>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 10 }}
                message={
                  <Text style={{ fontSize: 11.5 }}>
                    {cronogramaDoBanco
                      ? 'O cronograma deste contrato veio do banco. Valor, prazo, taxa, sistema, carência e data de contratação ficam travados aqui — recalcular pela fórmula jogaria fora a tabela verdadeira. Para atualizar as parcelas (reajuste de pós-fixado, por exemplo), reimporte a tabela do banco.'
                      : 'Há parcela já marcada como paga. Valor, prazo, taxa, sistema, carência e data de contratação ficam travados — mexer neles refaria o cronograma inteiro e reescreveria o que já foi pago. Desmarque a baixa em "Parcelas" se ela estiver errada.'}
                  </Text>
                }
              />
              <Card size="small" title={<Text style={{ fontSize: 12 }}>Cronograma atual</Text>}>
                <Row gutter={8}>
                  <Col span={12}>
                    <Statistic title="Parcelas" value={emp!.total_parcelas} valueStyle={{ fontSize: 14 }} />
                  </Col>
                  <Col span={12}>
                    <Statistic title="Já pagas" value={emp!.parcelas_pagas} valueStyle={{ fontSize: 14 }} />
                  </Col>
                  <Col span={12}>
                    <Statistic title="Juros contratados" value={parseFloat(emp!.juros_total)} precision={2} prefix="R$" valueStyle={{ fontSize: 14, color: '#B07500' }} />
                  </Col>
                  <Col span={12}>
                    <Statistic title="Saldo devedor" value={parseFloat(emp!.saldo_devedor_atual)} precision={2} prefix="R$" valueStyle={{ fontSize: 14 }} />
                  </Col>
                </Row>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Nome do banco, número do contrato, custos iniciais, IOF e observações continuam editáveis — nenhum deles mexe nas parcelas.
                </Text>
              </Card>
            </>
          ) : totais ? (
            <>
              <Card size="small" style={{ marginBottom: 10 }}>
                <Row gutter={8}>
                  <Col span={12}>
                    <Statistic title="Total a pagar" value={totais.parcela_sum} precision={2} prefix="R$" valueStyle={{ fontSize: 14 }} />
                  </Col>
                  <Col span={12}>
                    <Statistic title="Juros total" value={totais.juros} precision={2} prefix="R$" valueStyle={{ fontSize: 14, color: '#B07500' }} />
                  </Col>
                  <Col span={12}>
                    <Statistic title="Principal total" value={totais.principal} precision={2} prefix="R$" valueStyle={{ fontSize: 14 }} />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Custo efetivo"
                      value={(totais.parcela_sum / parseFloat(form.valor_principal) - 1) * 100}
                      precision={1}
                      suffix="%"
                      valueStyle={{ fontSize: 14 }}
                    />
                  </Col>
                </Row>
              </Card>

              {editando && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 10 }}
                  message={
                    <Text style={{ fontSize: 11.5 }}>
                      Ao salvar, as <strong>parcelas em aberto</strong> deste empréstimo são refeitas por este cronograma.
                    </Text>
                  }
                />
              )}
              {preview && preview.length > 0 && (
                <Card
                  size="small"
                  title={<Text style={{ fontSize: 12 }}>Cronograma proposto · {preview.length} parcelas</Text>}
                  styles={{ body: { padding: 0 } }}
                >
                  {/* Altura amarrada à viewport (regra da casa): usa o monitor
                      grande em vez de um teto fixo de 260px. */}
                  <div style={{ maxHeight: 'clamp(200px, calc(100vh - 430px), 620px)', overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, fontFamily: 'monospace' }}>
                      <thead>
                        <tr style={{ background: '#FAFBFC', position: 'sticky', top: 0 }}>
                          <th style={{ padding: 6, textAlign: 'center' }}>#</th>
                          <th style={{ padding: 6, textAlign: 'center' }}>Vencimento</th>
                          <th style={{ padding: 6, textAlign: 'right' }}>Juros</th>
                          <th style={{ padding: 6, textAlign: 'right' }}>Principal</th>
                          <th style={{ padding: 6, textAlign: 'right' }}>Parcela</th>
                          <th style={{ padding: 6, textAlign: 'right' }}>Saldo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.map(p => (
                          <tr key={p.numero}>
                            <td style={{ padding: 4, textAlign: 'center', color: '#666' }}>{p.numero}</td>
                            <td style={{ padding: 4, textAlign: 'center', color: '#666' }}>{fmtDate(p.data_vencimento)}</td>
                            <td style={{ padding: 4, textAlign: 'right', color: '#B07500' }}>{fmt(p.juros)}</td>
                            <td style={{ padding: 4, textAlign: 'right' }}>{fmt(p.principal)}</td>
                            <td style={{ padding: 4, textAlign: 'right', fontWeight: 500 }}>{fmt(p.parcela)}</td>
                            <td style={{ padding: 4, textAlign: 'right', color: '#888' }}>{fmt(p.saldo)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </>
          ) : (
            // Antes o lado direito ficava simplesmente vazio e ninguém sabia por
            // quê — a prévia só nasce com valor, prazo e taxa preenchidos.
            <Card size="small" style={{ textAlign: 'center', padding: '28px 12px' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Preencha <strong>valor principal</strong>, <strong>prazo</strong> e a <strong>taxa</strong> para
                o sistema montar o cronograma aqui.
              </Text>
            </Card>
          )}
        </Col>
        </Row>
      </Form>
    </Modal>
  );
}

function ModalParcelas({ emp, onClose, onChanged }: { emp: Emprestimo; onClose: () => void; onChanged: () => void }) {
  const { message } = AntApp.useApp();
  const [parcelas, setParcelas] = useState<Parcela[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { carregar(); }, []); // eslint-disable-line

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/emprestimos/${emp.id}/parcelas`, { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setParcelas(data);
    } finally { setLoading(false); }
  }

  // 🚨 A baixa PERGUNTA a data. O botão antigo carimbava sempre HOJE, e foi por
  // isso que as 20 primeiras parcelas da Mútua ficaram todas com 23/04/2026 —
  // o dia em que o empréstimo foi cadastrado — embora tenham sido pagas entre
  // novembro/2024 e maio/2026. O padrão sugerido é o VENCIMENTO da parcela,
  // que erra menos que "hoje".
  const [baixando, setBaixando] = useState<{ p: Parcela; data: string } | null>(null);

  async function salvarBaixa() {
    if (!baixando) return;
    try {
      const token = await getToken();
      await axios.patch(`${API_URL}/emprestimos/${emp.id}/parcelas/${baixando.p.numero}`, {
        paga: true, data_pagamento: baixando.data,
      }, { headers: { Authorization: token } });
      message.success(`Parcela ${baixando.p.numero} marcada como paga em ${fmtDate(baixando.data)}.`);
      setBaixando(null);
      await carregar();
      onChanged();
    } catch (e: any) {
      const d = e.response?.data;
      const corpo = d?.body ? (() => { try { return JSON.parse(d.body); } catch { return d; } })() : d;
      message.error(corpo?.error || 'Erro ao marcar parcela.', 8);
    }
  }

  async function desfazerBaixa(p: Parcela) {
    try {
      const token = await getToken();
      await axios.patch(`${API_URL}/emprestimos/${emp.id}/parcelas/${p.numero}`, {
        paga: false,
      }, { headers: { Authorization: token } });
      message.success(`Baixa da parcela ${p.numero} desfeita.`);
      await carregar();
      onChanged();
    } catch { message.error('Erro ao desfazer a baixa.'); }
  }

  const columns: ColumnsType<Parcela> = [
    { title: '#', dataIndex: 'numero', key: 'n', width: 50, align: 'center', render: (v) => <Text type="secondary">{v}</Text> },
    { title: 'Vencimento', dataIndex: 'data_vencimento', key: 'v', width: 100, align: 'center', render: (v) => <Text type="secondary">{fmtDate(v)}</Text> },
    { title: 'Juros', dataIndex: 'valor_juros', key: 'j', align: 'right', render: (v) => <Text style={{ color: '#B07500' }}>{fmt(parseFloat(v))}</Text> },
    { title: 'Principal', dataIndex: 'valor_principal', key: 'pr', align: 'right', render: (v) => fmt(parseFloat(v)) },
    { title: 'Parcela', dataIndex: 'valor_parcela', key: 'pa', align: 'right', render: (v) => <Text strong>{fmt(parseFloat(v))}</Text> },
    { title: 'Saldo', dataIndex: 'saldo_devedor', key: 's', align: 'right', render: (v) => <Text type="secondary">{fmt(parseFloat(v))}</Text> },
    {
      title: 'Status', key: 'st', width: 210, align: 'center',
      render: (_, r) => r.paga ? (
        <Space size={4}>
          <Tag color="success" icon={<CheckCircleFilled />} style={{ margin: 0 }}>
            Pago {fmtDate(r.data_pagamento)}
          </Tag>
          <Button size="small" type="text" icon={<EditOutlined />} title="Corrigir a data do pagamento"
            onClick={() => setBaixando({ p: r, data: String(r.data_pagamento || r.data_vencimento).slice(0, 10) })} />
          <Button size="small" type="text" icon={<UndoOutlined />} title="Desfazer a baixa"
            onClick={() => desfazerBaixa(r)} />
        </Space>
      ) : (
        <Button size="small" icon={<CheckCircleOutlined />}
          onClick={() => setBaixando({ p: r, data: String(r.data_vencimento).slice(0, 10) })}>
          Marcar paga
        </Button>
      ),
    },
  ];

  return (
    <Modal
      open
      onCancel={onClose}
      footer={[<Button key="close" onClick={onClose}>Fechar</Button>]}
      width="94vw"
      style={{ maxWidth: 1100, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' } }}
      title={
        <div>
          <Text strong style={{ color: '#020D7F' }}>Cronograma: {emp.instituicao}</Text>
          <div style={{ fontSize: 11, color: '#888', marginTop: 4, fontWeight: 400 }}>
            {emp.identificador} · {emp.sistema} · {emp.prazo_meses}m a {parseFloat(emp.taxa_mensal_pct).toFixed(3)}%/mês
          </div>
        </div>
      }
      destroyOnClose
    >
      <Table
        rowKey="id"
        columns={columns}
        dataSource={parcelas}
        loading={loading}
        size="small"
        pagination={false}
        scroll={{ y: 'clamp(240px, calc(100vh - 300px), 720px)' }}
        rowClassName={(r) => r.paga ? 'row-paga' : ''}
      />
      <Modal
        open={!!baixando}
        title={baixando ? `Parcela ${baixando.p.numero} — data do pagamento` : ''}
        okText="Confirmar"
        cancelText="Cancelar"
        onCancel={() => setBaixando(null)}
        onOk={salvarBaixa}
        width={380}
        destroyOnClose
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          Em que dia esta parcela saiu do banco? A sugestão é o vencimento
          {baixando ? ` (${fmtDate(baixando.p.data_vencimento)})` : ''} — troque se foi outro dia.
        </Text>
        <DatePicker
          value={baixando?.data ? dayjs(baixando.data) : null}
          onChange={(d: Dayjs | null) => setBaixando(b => b && ({ ...b, data: d ? d.format('YYYY-MM-DD') : '' }))}
          style={{ width: '100%', marginTop: 10 }}
          format="DD/MM/YYYY"
        />
      </Modal>
    </Modal>
  );
}

export default function Emprestimos() {
  return (
    <AntApp>
      <EmprestimosInner />
    </AntApp>
  );
}
